import * as React from "react";
import get from "lodash/get";
import startCase from "lodash/startCase";

import "./LedgerActivity.css";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { formatUserFacingNumber } from "../../utils/number";
import {
  getIsLoadingLedgerActivity,
  getLedgerActivity,
} from "../../reducers/loyalty/methods";
import { LedgerActivityRecord } from "../../reducers/loyalty/ledgerActivity";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";

export interface LedgerActivityProps extends ComponentBaseProps {
  classNames: string;
  name: string;
  configName: string;
  style?: React.CSSProperties;
  borderStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  descriptionStyle?: React.CSSProperties;
  dateStyle?: React.CSSProperties;
  positiveValueStyle?: React.CSSProperties;
  negativeValueStyle?: React.CSSProperties;
  notAuthenticatedStyle?: React.CSSProperties;
  notAuthenticatedText?: string;
  noLedgerActivityText?: string;
  noLedgerActivityStyle?: React.CSSProperties;
  rowStyle?: React.CSSProperties;
  tableStyle?: React.CSSProperties;
  align?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  isLoading: ReturnType<typeof getIsLoadingLedgerActivity>;
  ledgerActivity: ReturnType<typeof getLedgerActivity>;
  testMode?: string;
}

// Constants
const MAX_ACTIVITY_ROWS_ON_MOBILE = 3;
const MOBILE_WIDTH_THRESHOLD = 600;
const MOBILE_BREAKPOINT = `(max-width: ${MOBILE_WIDTH_THRESHOLD}px)`;

export class LedgerActivity extends ComponentBase<LedgerActivityProps> {
  constructor(props: LedgerActivityProps) {
    super(props);
    this.tableRef = React.createRef<HTMLTableElement>();
  }

  // Activity table component ref
  private tableRef: React.RefObject<HTMLTableElement>;

  componentDidUpdate(prevProps: LedgerActivityProps): void {
    if (prevProps.ledgerActivity !== this.props.ledgerActivity) {
      this.setTableHeight(
        this.getTableHeightForNumberOfRows(MAX_ACTIVITY_ROWS_ON_MOBILE)
      );
    }
    super.componentDidUpdate(prevProps);
  }

  renderNoLedgerActivity = () => {
    return (
      <div style={this.props.noLedgerActivityStyle}>
        {this.props.noLedgerActivityText ||
          "Earn points to redeem them for rewards!"}
      </div>
    );
  };

  renderNotAuthenticated = () => {
    return (
      <div style={this.props.notAuthenticatedStyle}>
        {this.props.notAuthenticatedText || ""}
      </div>
    );
  };

  renderLedgerActivity = (ledgerActivity: LedgerActivityRecord[]) => {
    const {
      borderStyle,
      cellStyle,
      classNames,
      descriptionStyle,
      dateStyle,
      positiveValueStyle,
      negativeValueStyle,
      rowStyle,
      tableStyle,
    } = this.props;

    return (
      <table
        className={`LedgerActivity-container ${classNames || ""}`}
        style={{
          ...tableStyle,
          borderCollapse: "collapse",
        }}
        aria-label="Ledger Activity"
        cellPadding={0}
        cellSpacing={0}
        ref={this.tableRef}
      >
        <tbody>
          {ledgerActivity.map((record, index) => {
            const style =
              record.value > 0 ? positiveValueStyle : negativeValueStyle;

            return (
              <tr
                key={`${record.id}-${index}`}
                style={{ ...borderStyle, ...rowStyle }}
              >
                <td style={{ ...dateStyle, ...cellStyle }}>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {new Date(record.createdOn).toLocaleDateString()}
                  </div>
                </td>
                <td style={{ ...descriptionStyle, ...cellStyle }}>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {humanizeLedgerActivity(record)}
                  </div>
                </td>
                <td
                  style={{
                    ...style,
                    ...cellStyle,
                  }}
                >
                  <div>{formatValue(record)}</div>
                </td>
                <td style={{ ...dateStyle, ...cellStyle }}>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {record.expirationDate && record.expirationDate.trim()
                      ? new Date(record.expirationDate).toLocaleDateString()
                      : " "}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // tslint:disable-next-line: cyclomatic-complexity
  render() {
    const { data, isLoading, testMode, isEditing, style } = this.props;

    const ledgerActivity = isEditMode()
      ? exampleActivity
      : this.props.ledgerActivity || [];

    const isCustomerAuthenticated = get(data, "customer.authenticated", false);

    if (isEditing) {
      return (
        <div
          style={{ ...style }}
          id={this.props.configName}
          onDragStart={this.onDragStart}
          onMouseDown={this.onMouseDown}
          ref={(component) => (this.component = component)}
          className={`LedgerActivity-div-container isEditing`}
        >
          {testMode === "notAuthenticated" && this.renderNotAuthenticated()}
          {testMode === "noLedgerActivity" && this.renderNoLedgerActivity()}
          {(testMode === "ledgerActivity" || !testMode) &&
            this.renderLedgerActivity(ledgerActivity)}
        </div>
      );
    }

    if (isLoading) {
      return null;
    }

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        ref={(component) => (this.component = component)}
        className={`LedgerActivity-div-container`}
      >
        {!isCustomerAuthenticated && this.renderNotAuthenticated()}
        {isCustomerAuthenticated &&
          ledgerActivity.length === 0 &&
          this.renderNoLedgerActivity()}
        {isCustomerAuthenticated &&
          ledgerActivity.length > 0 &&
          this.renderLedgerActivity(ledgerActivity)}
      </div>
    );
  }

  private isMobileView = (): boolean => {
    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  };

  private setTableHeight = (height?: number) => {
    // Skip setting table height if height is not provided, in edit mode,
    // or not in mobile view
    if (!height || isEditMode() || !this.isMobileView()) {
      return;
    }

    if (this.tableRef && this.tableRef.current) {
      this.tableRef.current.style.maxHeight = `${height}px`;
    }

    if (this.component) {
      this.component.style.maxHeight = `${height}px`;
      this.component.style.overflow = "hidden";
    }
  };

  private getTableHeightForNumberOfRows = (
    numRows: number = MAX_ACTIVITY_ROWS_ON_MOBILE
  ) => {
    if (!this.tableRef || !this.tableRef.current) {
      return;
    }

    let totalHeight = 0;
    const tableRows = this.tableRef.current.querySelectorAll("tr");
    totalHeight = Array.from(tableRows)
      .slice(0, numRows)
      .reduce((sum, row) => sum + row.offsetHeight, 0);

    return totalHeight;
  };
}

function formatValue(record: LedgerActivityRecord) {
  const formattedValue = formatUserFacingNumber(record.value);

  const value = record.value > 0 ? `+${formattedValue}` : formattedValue;

  return value;
}

// tslint:disable-next-line: cyclomatic-complexity
export function humanizeLedgerActivity(record: LedgerActivityRecord) {
  if (record.sourceName) {
    return record.sourceName;
  }

  // lowercases and replaces non-alphanumeric chars with a space
  const description = record.description.toLowerCase().replace(/[\W_]+/g, " ");
  const currency = startCase(record.currency);

  // should generally be handled by sourceName but leaving for backwards compatibility
  if (description.includes("loyalty opt in")) {
    return "Joined Program";
  } else if (description.includes("sign up")) {
    return "Signed Up";
  } else if (description.includes("purchase")) {
    return "Purchase";
  } else if (description.includes("debiting via redeem request")) {
    return `${currency} Redeemed`;
  } else if (description.includes("debiting via reverse redeem request")) {
    return `${currency} Reversed`;
  } else if (description.includes("manual adjustment to ledger")) {
    if (record.value > 0) {
      return `${currency} Added`;
    }

    return `${currency} Adjusted`;
  } else {
    // Titleize
    const words = description.split(" ");
    for (let i = 0; i < words.length; i++) {
      // strip auto-added customer & friend from description
      if (["customer", "friend"].includes(words[i].toLowerCase())) {
        words[i] = "";
        continue;
      }
      const word = words[i][0];
      words[i] = word ? word.toUpperCase() + words[i].substr(1) : "";
    }

    return words.join(" ").trim();
  }
}

const exampleActivity: LedgerActivityRecord[] = [
  {
    id: "00000000-0000-0000-0000-000000000000",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: 1000.05,
    description: "Purchase",
    currency: "Points",
    expirationDate: "2022-12-16T01:45:37.362Z",
  },
  {
    id: "00000000-0000-0000-0000-000000000001",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -150,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000002",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000003",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000004",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "My Custom Event With Rewards",
    currency: "Points",
    sourceName: "My Custom Event",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000005",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000006",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000007",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
  {
    id: "00000000-0000-0000-0000-000000000008",
    createdOn: "2021-12-16T01:45:37.362Z",
    value: -100,
    description: "Points Redeemed",
    currency: "Points",
    expirationDate: " ",
  },
];
